import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import blockedIcon from '@/assets/images/delete-blocked.webp';
import enableIcon from '@/assets/images/enable.png';
import { useConfirm } from '@/components/feedback/dialog';
import { useToast } from '@/components/feedback/toast';
import { ApiError } from '@/lib/api/errors';
import {
  type DealerDeleteCounts,
  type DealerDeleteDecision,
  decideDealerDelete,
} from '@/lib/dealers/delete-decision';
import type { ValidationDetail } from '@/lib/validation/server-details';

import {
  createDealer,
  deleteDealer,
  getDealer,
  getDealerDeviceFilterOptions,
  setDealerStatus,
  updateDealer,
} from './api';
import {
  DEALER_ACTION_ERROR,
  DEALER_DELETE_COPY,
  DEALER_ERROR_CODES,
  DEALER_HAS_UNASSIGNED_DEVICES,
  DEALER_STATUS_COPY,
} from './constants';
import type {
  CreateDealerBody,
  DealerDeviceAllocation,
  DealerRow,
  DealerToggleStatus,
  UpdateDealerBody,
} from './types';

/** `all` is the listing DataTable's `tableId`, so invalidating it refetches the current page with
 *  its URL-synced page, size, search, filter and sort intact — "refresh in place" (118479, 118643).
 *  `usCities` sits outside `all` deliberately, so a write does not refetch open city pages. The
 *  detail query (09) keys under `all`, so every write refreshes it too (118643 AC5). */
export const dealerKeys = {
  all: ['dealers'] as const,
  usCities: (search: string) => ['dealers-us-cities', search] as const,
  detail: (id: string) => [...dealerKeys.all, 'detail', id] as const,
  deviceFilters: (id: string, allocation: DealerDeviceAllocation) =>
    [...dealerKeys.all, 'device-filters', id, allocation] as const,
};

/** Dealer detail header. A 404 (`DEALER_NOT_FOUND`) is an answer, not a blip: never retried. */
export function useDealerDetail(id: string) {
  return useQuery({
    queryKey: dealerKeys.detail(id),
    queryFn: () => getDealer(id),
    enabled: Boolean(id),
    retry: (count, error) => !(error instanceof ApiError && error.status === 404) && count < 1,
  });
}

/** The device filter drawer's options for one allocation. */
export function useDealerDeviceFilterOptions(id: string, allocation: DealerDeviceAllocation) {
  return useQuery({
    queryKey: dealerKeys.deviceFilters(id, allocation),
    queryFn: () => getDealerDeviceFilterOptions(id, allocation),
    enabled: Boolean(id),
  });
}

export function useCreateDealer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateDealerBody) => createDealer(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: dealerKeys.all }),
  });
}

/** Another admin deleted or amended the Dealer (BE §1.6): a handled outcome, not a failure. */
export const isDealerGone = (error: unknown) =>
  error instanceof ApiError &&
  (error.code === DEALER_ERROR_CODES.notFound || error.code === DEALER_ERROR_CODES.stale);

export function useUpdateDealer() {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: dealerKeys.all });
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateDealerBody }) => updateDealer(id, body),
    onSuccess: refresh,
    // AC4: a gone Dealer refreshes the listing; other failures leave it alone.
    onError: (error) => (isDealerGone(error) ? refresh() : undefined),
  });
}

/** Disable/Enable: confirm with the AC copy → PATCH → toast. Any failure toasts the one fixed string
 *  (not `error.message`, hence not `useDestructiveAction`) and changes nothing. Never rethrows, so the
 *  dialog closes either way. Disable keeps the dialog's default illustration, which is Figma's. */
export function useDealerStatusChange() {
  const qc = useQueryClient();
  const { confirm } = useConfirm();
  const toast = useToast();
  const mutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: DealerToggleStatus }) =>
      setDealerStatus(id, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: dealerKeys.all }),
  });

  return (row: DealerRow, status: DealerToggleStatus) => {
    const enabling = status === 'active';
    const copy = DEALER_STATUS_COPY[enabling ? 'enable' : 'disable'];
    return confirm({
      title: copy.title,
      subtitle: copy.subtitle,
      confirmText: copy.confirmText,
      danger: !enabling,
      // Figma 2051:518282, drawn in the dialog's 76px slot.
      icon: enabling ? (
        <img src={enableIcon} alt="" aria-hidden="true" width={76} height={76} />
      ) : undefined,
      onConfirm: async () => {
        try {
          await mutation.mutateAsync({ id: row.id, status });
        } catch {
          toast.error({ title: DEALER_ACTION_ERROR });
          return;
        }
        toast.success({ title: copy.success });
      },
    });
  };
}

/** A 409's `details` → counts, read by field; an absent or unparsable entry reads as 0. */
function countsFromDetails(details: unknown): DealerDeleteCounts {
  const list = (Array.isArray(details) ? details : []) as ValidationDetail[];
  const read = (field: string) => Number(list.find((d) => d.field === field)?.message) || 0;
  return {
    unassignedDeviceCount: read('unassignedDeviceCount'),
    assignedCustomerCount: read('assignedCustomerCount'),
  };
}

/** Delete: decided from the row before any request (122593, grilling Q6). Blocked → the pop-up with
 *  the counts and a single Close; permitted → "Delete dealer?" → DELETE → toast, listing refreshes in
 *  place. A 409 race re-decides on the 409's counts and flips to blocked; any other failure toasts the
 *  one fixed string and closes. Every outcome refreshes the listing, so a refused or vanished row
 *  shows the server's truth next time. Spread `modalProps` onto `<DeleteConfirmModal>`. */
export function useDealerDelete() {
  const qc = useQueryClient();
  const toast = useToast();
  const [pending, setPending] = useState<{ row: DealerRow; decision: DealerDeleteDecision } | null>(
    null,
  );
  const mutation = useMutation({
    mutationFn: (id: string) => deleteDealer(id),
    onSettled: () => qc.invalidateQueries({ queryKey: dealerKeys.all }),
  });

  const requestDelete = (row: DealerRow) => setPending({ row, decision: decideDealerDelete(row) });

  const onConfirm = async (): Promise<{ blocked: true } | void> => {
    if (!pending) return;
    try {
      await mutation.mutateAsync(pending.row.id);
    } catch (error) {
      if (error instanceof ApiError && error.code === DEALER_HAS_UNASSIGNED_DEVICES) {
        const decision = decideDealerDelete(countsFromDetails(error.details));
        if (decision.kind === 'blocked') {
          setPending({ row: pending.row, decision });
          return { blocked: true };
        }
      }
      toast.error({ title: DEALER_ACTION_ERROR });
      return;
    }
    toast.success({ title: DEALER_DELETE_COPY.success });
  };

  return {
    requestDelete,
    modalProps: {
      open: pending !== null,
      initialBlocked: pending?.decision.kind === 'blocked',
      title: DEALER_DELETE_COPY.title,
      body: DEALER_DELETE_COPY.body,
      confirmText: DEALER_DELETE_COPY.confirmText,
      blockedTitle: DEALER_DELETE_COPY.blockedTitle,
      blockedBody: pending?.decision.kind === 'blocked' ? pending.decision.body : '',
      blockedCloseText: DEALER_DELETE_COPY.blockedCloseText,
      blockedIcon: <img src={blockedIcon} alt="" aria-hidden="true" width={76} height={76} />,
      onConfirm,
      onClose: () => setPending(null),
    },
  };
}
